import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  UserPlus,
  FileText,
  Calendar,
  Clock,
  ArrowRight,
  Trophy,
  Users,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { formatDistanceToNow } from "date-fns";
import type { ActivityFeed as ActivityFeedType } from "@shared/schema";

interface ActivityWithActor extends ActivityFeedType {
  actor: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    profileImageUrl: string | null;
    title: string | null;
    company: string | null;
  } | null;
}

function getInitials(firstName: string | null, lastName: string | null): string {
  const first = firstName?.[0] || "";
  const last = lastName?.[0] || "";
  return (first + last).toUpperCase() || "?";
}

function getActivityIcon(type: string) {
  switch (type) {
    case "article_published":
      return FileText;
    case "event_created":
      return Calendar;
    case "connection_made":
      return UserPlus;
    case "challenge_launched":
      return Trophy;
    case "leader_joined":
      return Users;
    default:
      return Clock;
  }
}

function getActivityDescription(activity: ActivityWithActor): { title: string; description: string; link?: string } {
  const actorName = activity.actor
    ? `${activity.actor.firstName || ""} ${activity.actor.lastName || ""}`.trim() || "Someone"
    : "Someone";
  const metadata = activity.metadata as Record<string, any> | null;

  switch (activity.type) {
    case "article_published":
      return {
        title: `${actorName} published an article`,
        description: metadata?.title || "New article",
        link: `/media/${activity.subjectId}`,
      };
    case "event_created":
      return {
        title: "New event created",
        description: metadata?.title || "Upcoming event",
        link: `/summit/${activity.subjectId}`,
      };
    case "connection_made":
      return {
        title: `${actorName} made a new connection`,
        description: activity.actor?.company || "New connection",
      };
    case "challenge_launched":
      return {
        title: "New challenge launched",
        description: metadata?.title || "Innovation challenge",
        link: `/challenges/${activity.subjectId}`,
      };
    case "leader_joined":
      return {
        title: `${actorName} joined as a leader`,
        description: activity.actor?.title || "New leader",
        link: `/leaders/${activity.actorId}`,
      };
    default:
      return {
        title: "Platform activity",
        description: "Recent activity",
      };
  }
}

function ActivityItemSkeleton() {
  return (
    <div className="flex items-start gap-3 p-3" data-testid="skeleton-activity-item">
      <Skeleton className="h-10 w-10 rounded-full" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
    </div>
  );
}

export function ActivityFeed() {
  const { data: activities = [], isLoading } = useQuery<ActivityWithActor[]>({
    queryKey: ["/api/activity", 15],
  });

  return (
    <Card data-testid="card-activity-feed">
      <CardHeader className="flex flex-row items-center justify-between gap-2 pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Clock className="h-5 w-5 text-secondary" />
          Recent Activity
        </CardTitle>
        <Link href="/media">
          <Button variant="ghost" size="sm" data-testid="link-view-all-activity">
            View all
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading ? (
          <div className="space-y-1" data-testid="activity-loading">
            {[1, 2, 3, 4, 5].map((i) => (
              <ActivityItemSkeleton key={i} />
            ))}
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground" data-testid="text-no-activity">
            <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-1" data-testid="activity-list">
            {activities.map((activity) => {
              const IconComponent = getActivityIcon(activity.type);
              const { title, description, link } = getActivityDescription(activity);
              
              const content = (
                <div
                  className="flex items-start gap-3 p-3 rounded-md hover-elevate active-elevate-2"
                  data-testid={`activity-item-${activity.id}`}
                >
                  <Avatar className="h-10 w-10 flex-shrink-0" data-testid={`activity-avatar-${activity.id}`}>
                    <AvatarImage 
                      src={activity.actor?.profileImageUrl || undefined} 
                      alt={activity.actor?.firstName || "User"} 
                    />
                    <AvatarFallback className="bg-secondary/10 text-secondary">
                      <IconComponent className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium" data-testid={`activity-title-${activity.id}`}>
                      {title}
                    </p>
                    <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5" data-testid={`activity-desc-${activity.id}`}>
                      {description}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1" data-testid={`activity-time-${activity.id}`}>
                      {activity.createdAt
                        ? formatDistanceToNow(new Date(activity.createdAt), { addSuffix: true })
                        : "Just now"}
                    </p>
                  </div>
                </div>
              );

              return link ? (
                <Link key={activity.id} href={link} data-testid={`activity-link-${activity.id}`}>
                  {content}
                </Link>
              ) : (
                <div key={activity.id}>{content}</div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
